<template>
  <DetailsAsideLayout>
    <template v-slot:detailsContent>
      <div :class="$style.sectionRoot">
        <h2 :class="$style.sectionHeader">
          Package Settings
        </h2>
        <div :class="$style.sectionContent">
          Identify the Package ID from the
          <a
            href="https://chocolatey.org/"
            rel="noopener noreferrer"
            target="_blank"
          >
            chocolatey.org
          </a> community package repository to install software on Windows devices.
        </div>
        <div :class="$style.inputRow">
          <div :class="$style.flexSingleWidth">
            <InputValidator
              v-model="item.displayName"
              :config="{
                attribute: 'displayName',
                label: inputNames.name,
                name: inputNames.name,
                placeholder: 'Example: Google Chrome',
                value: item.displayName,
                validate: nameValidator
              }"
              :tooltip="nameTooltip"
              @InputValidatorModel="handleValueChange"
            />
          </div>
        </div>
        <div
          v-for="(setting, index) in item.settings"
          :key="index"
          :class="$style.inputRow"
        >
          <div :class="$style.flexSingleWidth">
            <InputValidator
              v-model="setting.packageId"
              :config="{
                attribute: 'packageId',
                label: inputNames.packageId,
                name: inputNames.packageId,
                placeholder: 'Example: googlechrome',
                value: setting.packageId,
                validate: 'required|regex:^\\S*$',
                disabled: getIsItemBeingUpdated(),
              }"
              :tooltip="packageIdTooltip"
              @InputValidatorModel="(value,key) => handleSettingsValueChange(value,key,index)"
            />
          </div>
          <div>
            <label :class="$style.label">
              Custom Package
            </label>
            <CheckboxWithLabel
              label="Create a custom package"
              name="location"
              :value="customPackageCheckbox"
              @change="updatePackageLocationVisibility(...arguments)"
            />
            <transition
              :name="$style.slideTransition"
            >
              <div v-if="customPackageCheckbox">
                <InputValidator
                  v-model="setting.location"
                  :config="{
                    attribute: 'location',
                    label: inputNames.location,
                    name: inputNames.location,
                    placeholder: 'Example: https://repourl.com',
                    value: setting.location,
                  }"
                  @InputValidatorModel="(value,key) => handleSettingsValueChange(value,key,index)"
                />
                <CopyWell
                  content="choco new -h"
                  label="Create a new package"
                  :rows="1"
                />
                <label :class="$style.label">
                  What to know before creating custom packages
                </label>
                <ul>
                  <li>Chocolatey is installed on this device.</li>
                  <li>
                    You have read <LinkExternal :href="customPackageKbArticle">
                      this Knowledge Base article
                    </LinkExternal> and understand how a package works.
                  </li>
                </ul>
              </div>
            </transition>
          </div>
        </div>
      </div>
      <div
        v-for="(setting, index) in item.settings"
        :key="index"
        :class="[$style.sectionRoot, $style.settingsContainer]"
      >
        <h2 :class="$style.sectionHeader">
          Installation Settings
        </h2>
        <div :class="$style.sectionContent">
          <div :class="$style.radioContent">
            <input
              :checked="setting.desiredState === 'INSTALL'"
              name="INSTALL"
              type="radio"
              @change="e => handleSettingsValueChange(e.target.name, 'desiredState', index)"
            >
            <div :class="$style.radioLabel">
              Install this software
            </div>
            <IconTooltip>
              Installs software and keeps software at the installed version.
            </IconTooltip>
            <transition
              :name="$style.slideTransition"
            >
              <div v-if="setting.desiredState === 'INSTALL'">
                <CheckboxWithLabel
                  :class="$style.autoUpdate"
                  label="Keep software package up to date"
                  name="autoUpdate"
                  :value="setting.autoUpdate"
                  @change="value => handleSettingsValueChange(value, 'autoUpdate', index)"
                />
                <transition
                  :name="$style.slideTransition"
                >
                  <CheckboxWithLabel
                    v-if="setting.autoUpdate"
                    :class="$style.allowUpdateDelay"
                    :disabled="!setting.autoUpdate"
                    label="Allow end users to delay updates for up to one week"
                    name="allowUpdateDelay"
                    :tooltip="allowUpdateDelayTooltip"
                    :value="setting.allowUpdateDelay"
                    @change="value => handleSettingsValueChange(value, 'allowUpdateDelay', index)"
                  />
                </transition>
              </div>
            </transition>
          </div>
          <div
            :class="$style.radioContent"
          >
            <input
              :checked="setting.desiredState === 'UNINSTALL'"
              name="UNINSTALL"
              type="radio"
              @change="handleUninstallSelected(index)"
            >
            <div :class="$style.radioLabel">
              Uninstall this software
            </div>
            <IconTooltip>
              Removes the software, its data, and its associations.
            </IconTooltip>
          </div>
        </div>
      </div>
      <div :class="$style.legalFooter">
        The community public repository hosted at chocolatey.org and any installation scripts
        and packages from that repository are not components of the JumpCloud Service
        and are not supported by JumpCloud.  You are responsible for any risks with respect to,
        and for obtaining necessary rights to, any third-party software
        you download or install using this functionality.
      </div>
    </template>
  </DetailsAsideLayout>
</template>

<script>
import {
  ItemTypeConsts, StoreModelMap,
} from 'client/util/Constants';
import { cloneDeep, get } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import CheckboxWithLabel from 'client/components/FormValidation/CheckboxWithLabel.vue';
import CopyWell from 'client/components/FormValidation/CopyWell';
import DetailsAsideLayout from 'client/components/AsidePanel/Details/DetailsAsideLayout.vue';
import IconTooltip from 'client/components/Tooltip/IconTooltip';
import InputValidator from 'client/components/FormValidation/InputValidator';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import LinkExternal from 'client/components/Links/LinkExternal';
import ModalEventBus from 'client/components/Modal/ModalEventBus';

const softwareAppsModel = StoreModelMap[ItemTypeConsts.softwareApps];

export default {
  name: 'SoftwareAppsDetails',

  inject: ['$validator'],

  components: {
    IconTooltip,
    LinkExternal,
    CheckboxWithLabel,
    CopyWell,
    DetailsAsideLayout,
    InputValidator,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  vValidateDict: {
    custom: {
      'Software Name': {
        required: '* Name field empty.',
        regex: '* Name field cannot use the following characters: <>\'"&',
      },
      'Package ID': {
        required: '* Package ID field empty.',
        regex: '* Package ID field cannot have spaces.',
      },
    },
  },

  ...mapState(softwareAppsModel, {
    item: state => state.item,
  }),

  data() {
    return {
      nameTooltip: {
        text: 'Your unique display name for the managed software.',
      },
      nameValidator: 'required|regex:^[^<>&\'"]*$',
      packageIdTooltip: {
        text: 'You find this by going to the <a rel="noopener noreferrer" target="_blank" href="https://chocolatey.org/">chocolatey.org</a>  community package repository (<a rel="noopener noreferrer" target="_blank" href="https://chocolatey.org/packages">https://chocolatey.org/packages</a>), searching the repository for the package, and from the installation command, copying only the package name. <br/>For example: to install <b>Google Chrome 84.0.4147.125</b> use a <b>Package ID</b> of <b>googlechrome</b>. </br>Don’t include any spaces, flags or release numbers. We’ll install the latest version available. </br></br> <em> *These Package IDs correspond to the package IDs in the community public repository hosted at <a rel="noopener noreferrer" target="_blank" href="https://chocolatey.org/">chocolatey.org</a>.  Any installation scripts and packages from that repository are not components of the JumpCloud Service.  You are responsible for testing those scripts and packages and for any third-party software you download or install using this functionality </em>',
      },
      allowUpdateDelayTooltip: {
        text: 'Updates can be delayed for one week.',
      },
      inputNames: {
        name: 'Software Name',
        packageId: 'Package ID',
        location: 'Repository URL',
      },
      customPackageCheckbox: false,
      customPackageKbArticle: KbArticleUrls.managedSoftwareCustomPackage,
    };
  },

  computed: {
    ...mapGetters('FeatureFlagsModule', ['betaFeatures']),
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  mounted() {
    this.$validator.localize('en', this.$options.vValidateDict);
    const location = get(this.item, 'settings[0].location');
    this.customPackageCheckbox = !!location;
  },

  methods: {

    getIsItemBeingUpdated() {
      return this.isItemBeingUpdated;
    },

    handleSettingsValueChange(value, key, index) {
      const updatedSettings = cloneDeep(this.item.settings);
      updatedSettings[index][key] = value;

      if (key === 'autoUpdate' && !value) {
        updatedSettings[index].allowUpdateDelay = false;
      }
      // resets child settings
      if (key === 'desiredState') {
        updatedSettings[index].autoUpdate = false;
        updatedSettings[index].allowUpdateDelay = false;
      }

      this.setItemField({ key: 'settings', value: updatedSettings });
    },

    // Updates the model and shows an uninstall informational modal
    handleUninstallSelected(index) {
      this.handleSettingsValueChange('UNINSTALL', 'desiredState', index);

      const mdTemplate = '**Are you sure** you want to uninstall this software? Uninstalling permanently removes all software data and associations. You need to save this action to uninstall this software.\n\n*Software that is uninstalled is still managed by JumpCloud.*';

      // no need to await for the modal as it's informational only at the moment
      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps: {
          mdTemplate,
          headerText: 'Uninstall Software',
          continueText: 'OK',
          cancelText: '',
          mainActionAttention: true,
          handleContinue: ModalEventBus.close,
        },
      });
    },

    handleValueChange(value, key) {
      this.setItemField({ key, value });
    },

    updatePackageLocationVisibility(value) {
      this.customPackageCheckbox = !this.customPackageCheckbox;
      if (!value) {
        // reset location to empty text
        this.handleSettingsValueChange('', 'location', 0);
      }
    },
  },
};
</script>

<style module>
@import '../css/sections.css';
@import '../components/FormValidation/css/formFields.css';

@value (slowEaseFade) from '~css/transitions.css';

.allowUpdateDelay {
  margin-left: 45px;
  margin-top: 5px;
}

.autoUpdate {
  margin-left: 25px;
  margin-top: 5px;
}

.radioContent {
  margin-bottom: 5px;
}

.radioLabel {
  display: inline;
  font-weight: normal;
  margin-left: 8px;
}

.settingsContainer {
  height: 170px;
}

.slideTransition,
.slideTransition-enter-active,
.slideTransition-leave-active {
  transition: slowEaseFade;
}

.slideTransition-enter,
.slideTransition-leave-to {
  opacity: 0;
  transform: translateY(-1em);
}

.legalFooter {
  color: var(--jcGrey600);
  font-size: 0.8rem;
  padding-top: 10px;
}

</style>
